import { datadogLogs } from '@datadog/browser-logs'
import { Grid } from '@focaldata/cin-ui-components'
import React, { useEffect, useRef, useState } from 'react'
import { DialogRawData } from '../../../controls/DialogRawData'
import SpinnerDialog from '../../../controls/SpinnerDialog/SpinnerDialog'
import { DataFormat } from '../../../data/model/shared'
import { useProjectId } from '../../../hooks/useProjectId'
import { getCombinedRawData } from '../../../hooks/useRawDataDownload'
import { downloadFile, getNextText } from '../../../utils/projectUtils'

interface Props {
  isDialogOpen: boolean
  setIsDialogOpen: (value: boolean) => void
}

const INITIAL_SPINNER_TEXT = 'Building columns'

/**
 * Combined (multi-market) raw-data export dialog: the project-scoped sibling of
 * RequestRawData. It downloads the pooled respondent data for the whole project
 * (getCombinedRawData -> POST /artifacts/multimarket/{projectId}/download)
 * rather than a single survey, reusing the same format picker and spinner UX.
 */
const RequestCombinedRawData: React.FC<Props> = (props: Props) => {
  const { isDialogOpen, setIsDialogOpen }: Props = props
  const projectId = useProjectId()
  const [gettingRawData, setGettingRawData] = useState<boolean>(false)
  const [spinnerText, setSpinnerText] = useState<string>(INITIAL_SPINNER_TEXT)

  // The spinner cycles its label on an interval and auto-closes on failure via a
  // timeout; both are held in refs so they can be cleared on every exit path
  // (success, failure and unmount) rather than leaking a timer for the life of
  // the (always-mounted) component.
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearSpinnerInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const startSpinner = () => {
    // Reset any stale text from a previous failed attempt so a fresh, in-flight
    // download never briefly shows the old error message.
    setSpinnerText(INITIAL_SPINNER_TEXT)
    setGettingRawData(true)
    clearSpinnerInterval()
    intervalRef.current = setInterval(() => {
      setSpinnerText(getNextText())
    }, 2000)
  }

  useEffect(() => {
    return () => {
      clearSpinnerInterval()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleDownload: (fileFormat: DataFormat) => Promise<void> = async (
    fileFormat
  ) => {
    startSpinner()
    try {
      const url = await getCombinedRawData(projectId, fileFormat)
      downloadFile({ dataUrl: url })
      setGettingRawData(false)
      setIsDialogOpen(false)
    } catch (error) {
      setSpinnerText(
        'The file failed to download, please retry - If the download continues to fail, please contact support@focaldata.com'
      )
      datadogLogs.logger.error(
        `The combined raw data file failed to download for project: ${projectId}`,
        { error }
      )

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        setGettingRawData(false)
        setIsDialogOpen(false)
      }, 3000)
    } finally {
      clearSpinnerInterval()
    }
  }

  return (
    <>
      <DialogRawData
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false)
        }}
        onRequestData={(_, fileFormat) => handleDownload(fileFormat)}
        loading={false}
      />
      <SpinnerDialog
        text={spinnerText}
        subtext={
          <Grid container direction="column" alignItems="center">
            <Grid item>DO NOT REFRESH THIS PAGE.</Grid>
            <Grid item textAlign="center">
              Exporting your file will take 3-4 minutes on average and up to
              15-20 minutes for large sample size.
            </Grid>
          </Grid>
        }
        isVisible={gettingRawData}
      />
    </>
  )
}

export default RequestCombinedRawData
